/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ApiError } from '@/client';
import { PageHeader } from '@/components/PageHeader';
import {
  useActivityWindow,
  useRefreshTenantActivity,
  useTenantActivityInfiniteQuery,
} from '@/hooks/query/useTenantActivityInfiniteQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { ActivityTable } from '@/pages/Activity/ActivityTable';
import { ActivityWindowSelect } from '@/pages/Activity/ActivityWindowSelect';

import './activity.css';

function formatRelative(ms: number): string {
  const delta = Date.now() - ms;
  if (delta < 60_000) return 'Less than a minute ago';
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
}

function describeState(loading: boolean, loadedAt: number | null, loadingLabel: string): string {
  if (loading) return loadingLabel;
  if (loadedAt) return formatRelative(loadedAt);
  return 'Never';
}

function newest(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return a > b ? a : b;
}

function errorMessage(err: ApiError | null, fallback: string): string | undefined {
  if (!err) return undefined;
  if (err.body && typeof err.body === 'object') {
    const bodyMessage = (err.body as Record<string, unknown>).message;
    if (typeof bodyMessage === 'string' && bodyMessage.trim()) {
      return bodyMessage;
    }
  }
  return err.message ?? fallback;
}

export function ActivityPage() {
  const { tenantID } = useParams<{ tenantID: string }>();
  const { t } = useMimicTranslation('activity');
  const windowState = useActivityWindow();
  const refresh = useRefreshTenantActivity(tenantID);

  const hallmarks = useTenantActivityInfiniteQuery({
    tenantId: tenantID,
    section: 'hallmarks',
    activityWindow: windowState.activityWindow,
  });

  const deflections = useTenantActivityInfiniteQuery({
    tenantId: tenantID,
    section: 'deflections',
    activityWindow: windowState.activityWindow,
  });

  const hallmarksRows = useMemo(() => hallmarks.data?.pages.flatMap((p) => p.data) ?? [], [hallmarks.data]);
  const deflectionsRows = useMemo(() => deflections.data?.pages.flatMap((p) => p.data) ?? [], [deflections.data]);

  const loading = hallmarks.isLoading || deflections.isLoading;

  const loadedAt = newest(
    hallmarks.dataUpdatedAt > 0 ? hallmarks.dataUpdatedAt : null,
    deflections.dataUpdatedAt > 0 ? deflections.dataUpdatedAt : null,
  );

  return (
    <div className="activity-page-outer">
      <div className="activity-page" data-testid="activity-page">
        <PageHeader
          level={2}
          title={t('title')}
          text={
            <span className="activity-last-updated">
              <span>
                {t('lastUpdated')} {describeState(loading, loadedAt, t('loading'))}
              </span>
              <button
                type="button"
                className="activity-refresh"
                onClick={refresh}
                disabled={loading}
                data-testid="activity-refresh"
              >
                {t('actions.refresh')}
              </button>
            </span>
          }
        >
          <ActivityWindowSelect value={windowState.activityWindow} onChange={windowState.setActivityWindow} />
        </PageHeader>
        <ActivityTable
          title={t('sections.hallmarks')}
          rows={hallmarksRows}
          isLoading={hallmarks.isLoading}
          isFetchingNextPage={hallmarks.isFetchingNextPage}
          hasNextPage={!!hallmarks.hasNextPage}
          isError={hallmarks.isError}
          errorMessage={errorMessage(hallmarks.error, t('failedToLoad'))}
          fetchNextPage={hallmarks.fetchNextPage}
          tenantId={tenantID!}
        />
        <ActivityTable
          title={t('sections.deflections')}
          rows={deflectionsRows}
          isLoading={deflections.isLoading}
          isFetchingNextPage={deflections.isFetchingNextPage}
          hasNextPage={!!deflections.hasNextPage}
          isError={deflections.isError}
          errorMessage={errorMessage(deflections.error, t('failedToLoad'))}
          fetchNextPage={deflections.fetchNextPage}
          tenantId={tenantID!}
        />
      </div>
    </div>
  );
}
